// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import { Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { EducBox, ChartBox, ResultCard, InputRow, EDUC, baseChartOpts, showToastOnce, fmt } from './shared';

const ShareBtn = ({ text }) => {
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ text, url: 'https://finsapiens.it' }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(text + '\nhttps://finsapiens.it'); toast.success('Copiato negli appunti ✓'); } catch { toast.error('Errore copia'); }
    }
  };
  return (
    <button onClick={share} data-testid="fire-share-btn" className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg transition-colors hover:bg-white/[0.04]" style={{ border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C' }}>
      <Share2 className="w-3 h-3" />Condividi il risultato
    </button>
  );
};

const FIRECalc = ({ onStepComplete }) => {
  const navigate = useNavigate();
  const [speseAnnuali, setSpeseAnnuali] = useState('30000');
  const [patrimonioAttuale, setPatrimonioAttuale] = useState('50000');
  const [risparmioAnnuale, setRisparmioAnnuale] = useState('15000');
  const [rendimento, setRendimento] = useState('7');
  // Deployment 14 — P4: SWR modificabile (default 3.5%, range 2-5%)
  const [swr, setSwr] = useState('3.5');

  const S = parseFloat(speseAnnuali) || 0;
  const PA = parseFloat(patrimonioAttuale) || 0;
  const RA = parseFloat(risparmioAnnuale) || 0;
  const r = parseFloat(rendimento) / 100;
  const swrPct = Math.min(5, Math.max(2, parseFloat(swr) || 3.5)) / 100;
  // Deployment 14 — capitale_target = spese_annue / SWR (default 3.5%)
  const patrimonioObiettivo = swrPct > 0 ? S / swrPct : 0;
  let anni = 0;
  let patrimonio = PA;
  while (patrimonio < patrimonioObiettivo && anni < 100) {
    patrimonio = patrimonio * (1 + r) + RA;
    anni++;
  }
  const raggiunto = patrimonio >= patrimonioObiettivo;
  const annoTarget = new Date().getFullYear() + anni;
  const shareText = `Ho calcolato che posso raggiungere la libertà finanziaria nel ${annoTarget}. E tu quando? 🔥`;

  useEffect(() => {
    if (patrimonioObiettivo > 0 && raggiunto) {
      showToastOnce('fire', () => {
        toast('Prossimo passo', {
          description: 'Hai un piano pensionistico complementare? Scopri quanto ti manca →',
          action: { label: 'Calcola ora', onClick: () => navigate('/calcolatori/pensione-complementare') },
          duration: 5000,
        });
      });
      if (onStepComplete) onStepComplete();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raggiunto]);

  const labelsFire = Array.from({ length: Math.min(anni + 1, 51) }, (_, i) => i % 5 === 0 ? `Anno ${i}` : '');
  const dataFire = [];          // patrimonio totale (capitale + interessi)
  const dataVersato = [];       // solo capitale versato cumulativo
  let p = PA;
  let invested = PA;
  for (let i = 0; i <= Math.min(anni, 50); i++) {
    dataFire.push(Math.round(p));
    dataVersato.push(Math.round(invested));
    p = p * (1 + r) + RA;
    invested = invested + RA;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <EducBox text={EDUC.fire} />
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <InputRow label="Spese annuali (€)" value={speseAnnuali} onChange={setSpeseAnnuali} prefix="€" step={100} min={0} max={10000000} />
        <InputRow label="Patrimonio attuale (€)" value={patrimonioAttuale} onChange={setPatrimonioAttuale} prefix="€" step={100} min={0} max={10000000} />
        <InputRow label="Risparmio annuale (€)" value={risparmioAnnuale} onChange={setRisparmioAnnuale} prefix="€" step={100} min={0} max={10000000} />
        <InputRow label="Rendimento atteso (%)" value={rendimento} onChange={setRendimento} suffix="%" step={0.1} min={0} max={30} />
        <InputRow label="Safe Withdrawal Rate (SWR)" value={swr} onChange={setSwr} suffix="%" step={0.1} min={2} max={5} tooltip="Tasso di prelievo sicuro: la regola del 4% è basata su dati USA — per portafogli europei si consiglia 3-3.5%" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <ResultCard label={`Patrimonio obiettivo (SWR ${swrPct*100}%)`} value={`€${fmt(patrimonioObiettivo)}`} green />
        <ResultCard label="Anni al FIRE" value={raggiunto ? `${anni} anni` : 'Aumenta risparmio'} gold />
        <ResultCard label="Patrimonio attuale" value={`€${fmt(PA)}`} />
        <ResultCard label="Rendita annua passiva" value={`€${fmt(patrimonioObiettivo * swrPct)}`} green />
      </div>
      {raggiunto && <div className="mt-3 mb-1 flex justify-end"><ShareBtn text={shareText} /></div>}
      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-xs text-[#888780]">
        <p data-testid="fire-note-swr" className="p-3 rounded-lg" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
          <strong className="text-[#C9A84C]">Nota SWR.</strong> La regola del 4% è basata su dati storici USA — per portafogli europei considera un tasso conservativo del 3-3.5%
        </p>
        <p data-testid="fire-note-tax" className="p-3 rounded-lg" style={{ background: 'rgba(61,122,82,0.06)', border: '1px solid rgba(61,122,82,0.2)' }}>
          <strong className="text-[#3D7A52]">Tassazione.</strong> La tassazione del 26% si applica alle plusvalenze al momento della vendita delle quote — non annualmente. Gli ETF ad accumulo non distribuiscono dividendi tassabili ogni anno
        </p>
      </div>
      <ChartBox title="Crescita verso la libertà finanziaria" height={420}>
        <Line
          data={{
            labels: labelsFire,
            datasets: [
              // Gold = patrimonio totale (capitale + interessi)
              { label: 'Patrimonio (con interessi)', data: dataFire, borderColor: '#C9A84C', backgroundColor: 'rgba(201,168,76,0.15)', fill: true, tension: 0.3, pointRadius: 0 },
              // Grey = solo capitale versato cumulativo
              { label: 'Capitale versato', data: dataVersato, borderColor: '#888780', backgroundColor: 'rgba(136,135,128,0.10)', borderDash: [4, 4], fill: true, pointRadius: 0 },
            ],
          }}
          options={{ ...baseChartOpts(), plugins: { ...baseChartOpts().plugins, legend: { display: true, labels: { color: '#888780' } } } }}
        />
      </ChartBox>
</motion.div>
  );
};

export default FIRECalc;
